import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, ChevronRight, FileDown, Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/lib/utils";
import { RecordService, readBlobError, triggerBlobDownload } from "@/services/recordService";

const BYTES_PER_AI_DECISION = 100 * 1024;
const BYTES_PER_EMAIL = 150 * 1024;
const BYTES_PER_OTHER_ROW = 2 * 1024;
const BYTES_PER_DOC_FALLBACK = 400 * 1024;

const BYTE_KEYS = ["size", "sizeBytes", "byteSize", "bytes", "fileSize"];

const sumBytes = (rows = []) =>
  rows.reduce((acc, row) => {
    if (!row || typeof row !== "object") return acc;
    for (const key of BYTE_KEYS) {
      const v = row[key];
      if (typeof v === "number" && Number.isFinite(v) && v > 0) return acc + v;
    }
    return acc;
  }, 0);

const formatEstimate = (bytes) => {
  if (bytes >= 1024 * 1024) return `~${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `~${Math.max(1, Math.round(bytes / 1024))} KB`;
  return "~1 KB";
};

const estimateSeconds = (bytes) => {
  const seconds = Math.round(5 + bytes / (1024 * 1024));
  if (seconds < 60) return `about ${seconds} seconds`;
  return `about ${Math.round(seconds / 60)} minutes`;
};

/**
 * What is this export for?
 *
 * Two rules the dialog must hold:
 * - Unmasking is a decision, not a default. On the outside preset, ticking
 *   off "Hide colleagues' names" is recorded in the export audit row and
 *   stated on the bundle's cover page — the dialog says so beside the checkbox.
 * - `inExportByDefault: false` sections start unticked (`duplicates` is other
 *   people's records; a bundle leaving the building should not carry them
 *   unless someone chose that).
 *
 * The size is estimated client-side from the section counts plus the document
 * byte sizes read from the `resumes` and `compliance` sections — an export
 * that turns out to be 200 MB after four minutes of waiting is the experience
 * this avoids.
 */
const ExportDialog = ({ open, onOpenChange, subjectType, subjectId, sections = [], applicationId, tenantId }) => {
  const defaultPreset = subjectType === "user" ? "internal" : "outside";
  const [preset, setPreset] = useState(defaultPreset);
  const [maskNames, setMaskNames] = useState(defaultPreset === "outside");
  const [maskPii, setMaskPii] = useState(false);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [sectionChecks, setSectionChecks] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [docBytes, setDocBytes] = useState({});

  // Reset the form every time the dialog opens (or the subject changes).
  useEffect(() => {
    if (!open) return;
    const next = defaultPreset;
    setPreset(next);
    setMaskNames(next === "outside");
    setMaskPii(false);
    setIncludeFiles(true);
    setError(null);
    setExporting(false);
    setAdvancedOpen(false);
    setSectionChecks(Object.fromEntries(sections.map((s) => [s.key, s.inExportByDefault !== false])));
  }, [open, defaultPreset, sections]);

  // Preset switch moves the mask default. The manual toggle overrides it —
  // until the next preset switch, which is a deliberate re-decision.
  const changePreset = (next) => {
    setPreset(next);
    setMaskNames(next === "outside");
  };

  // Document byte sizes for the estimate — read once when the dialog opens.
  useEffect(() => {
    if (!open || subjectType !== "candidate") return;
    let cancelled = false;
    const wanted = sections.filter((s) => s.key === "resumes" || s.key === "compliance");
    Promise.all(
      wanted.map((s) =>
        RecordService.getSection({
          subjectType,
          subjectId,
          section: s.key,
          page: 1,
          perPage: 200,
          applicationId,
          tenantId,
        }).catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      setDocBytes(Object.fromEntries(wanted.map((s, i) => [s.key, sumBytes(results[i]?.rows)])));
    });
    return () => {
      cancelled = true;
    };
  }, [open, subjectType, subjectId, applicationId, tenantId, sections]);

  const estimatedBytes = useMemo(() => {
    let bytes = 0;
    for (const s of sections) {
      if (!sectionChecks[s.key]) continue;
      const count = s.count ?? 0;
      if (s.key === "ai_decisions") bytes += count * BYTES_PER_AI_DECISION;
      else if (s.key === "emails") bytes += count * BYTES_PER_EMAIL;
      else if (s.key === "resumes" || s.key === "compliance") bytes += docBytes[s.key] ?? count * BYTES_PER_DOC_FALLBACK;
      else bytes += count * BYTES_PER_OTHER_ROW;
    }
    return bytes;
  }, [sections, sectionChecks, docBytes]);

  const unmaskWarning = preset === "outside" && !maskNames && "This choice is recorded in the export audit trail and stated on the bundle's cover page.";

  // Untick everything and the joined value is "", which the router reads as
  // "not given" and answers with the preset's default set — a dozen sections
  // the operator turned off. Refused here instead.
  const chosenSections = useMemo(() => sections.filter((s) => sectionChecks[s.key]).map((s) => s.key), [sections, sectionChecks]);

  const handleExport = async () => {
    if (exporting) return;

    if (chosenSections.length === 0) {
      setError("Choose at least one section to export.");
      return;
    }

    setExporting(true);
    setError(null);

    const params = {
      preset,
      sections: chosenSections.join(","),
      includeFiles: includeFiles ? "true" : "false",
      maskNames: maskNames ? "true" : "false",
      maskPii: maskPii ? "true" : "false",
    };

    if (applicationId) {
      params.application_id = applicationId;
    }

    try {
      const { blob, filename } = await RecordService.exportBundle({
        subjectType,
        subjectId,
        params,
        tenantId,
      });

      if (!blob) {
        throw new Error("EXPORT_BLOB_MISSING");
      }

      triggerBlobDownload(blob, filename || `${subjectType}-record-export.zip`);

      toast.success("Export complete — download started.");
      onOpenChange(false);
    } catch (err) {
      console.error("Record export failed:", err);

      const status = err?.response?.status;
      const data = await readBlobError(err);

      if (status === 429 || data?.error_code === "RATE_LIMIT_EXCEEDED") {
        setError("You have reached the export limit (5 per minute). An export reads many tables and renders PDFs, so wait a moment before trying again.");
      } else if (status === 413 || data?.error_code === "RECORD_EXPORT_TOO_LARGE") {
        setError(data?.message || "This export is too large (over 200 MB).");
      } else if (data?.error_code === "INSUFFICIENT_PERMISSIONS") {
        setError(data?.message || "You don't have permission to export records.");
      } else {
        setError(data?.message || "The export failed. Please try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !exporting) onOpenChange(false);
      }}
    >
      <DialogContent className="w-[420px] md:w-[620px] flex flex-col max-h-[90vh] overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">Export this record</DialogTitle>
          <DialogDescription className="text-[12px] text-text-primary/70">Everything this platform holds on the person, as one bundle.</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4 custom-scrollbar">
          <fieldset>
            <legend className="mb-2 text-[13px] font-semibold text-text-primary">What is this export for?</legend>

            <label
              className={cn(
                "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3.5 py-3 transition-colors",
                preset === "outside" ? "border-tone-blue bg-tone-blue/10" : "border-border-primary hover:bg-background-tertiary/40",
              )}
            >
              <Input
                type="radio"
                name="export-preset"
                value="outside"
                checked={preset === "outside"}
                onChange={() => changePreset("outside")}
                className="mt-0.5 size-3.5 w-auto appearance-auto rounded-none border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tone-blue"
              />
              <span>
                <span className="block text-[13px] font-medium text-text-primary">Share outside the organization</span>
                <span className="mt-0.5 block text-[12px] leading-5 text-text-secondary">Colleagues' names are replaced with labels (Colleague A, Colleague B).</span>
              </span>
            </label>

            <label
              className={cn(
                "mt-2 flex cursor-pointer items-start gap-2.5 rounded-lg border px-3.5 py-3 transition-colors",
                preset === "internal" ? "border-tone-blue bg-tone-blue/10" : "border-border-primary hover:bg-background-tertiary/40",
              )}
            >
              <Input
                type="radio"
                name="export-preset"
                value="internal"
                checked={preset === "internal"}
                onChange={() => changePreset("internal")}
                className="mt-0.5 size-3.5 w-auto appearance-auto rounded-none border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tone-blue"
              />
              <span>
                <span className="block text-[13px] font-medium text-text-primary">Internal</span>
                <span className="mt-0.5 block text-[12px] leading-5 text-text-secondary">Everything, with real names.</span>
              </span>
            </label>
          </fieldset>

          <div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAdvancedOpen((v) => !v)}
              aria-expanded={advancedOpen}
              className="!h-auto w-auto gap-1.5 !rounded-md !px-1.5 !py-1 text-[12px] font-medium text-text-secondary hover:bg-background-tertiary/40 hover:text-text-primary"
            >
              {advancedOpen ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
              Advanced
            </Button>

            {advancedOpen && (
              <div className="mt-3 flex flex-col gap-3">
                <label className="flex cursor-pointer items-center gap-2.5 text-[12px] text-text-primary">
                  <Checkbox checked={includeFiles} disabled={maskPii} onCheckedChange={(next) => setIncludeFiles(next === true)} />
                  Include original documents (CVs, compliance files)
                </label>

                <label className="flex cursor-pointer items-start gap-2.5 text-[12px] text-text-primary">
                  <Checkbox checked={maskNames} onCheckedChange={(next) => setMaskNames(next === true)} className="mt-0.5" />
                  <span>
                    <span className="block">Hide colleagues' names</span>
                    {unmaskWarning && (
                      <span className="mt-1 block rounded-md border border-tone-amber/30 bg-tone-amber-soft px-2.5 py-1.5 text-[11px] leading-4 text-text-primary">
                        {unmaskWarning}
                      </span>
                    )}
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2.5 text-[12px] text-text-primary">
                  <Checkbox
                    checked={maskPii}
                    onCheckedChange={(next) => {
                      const on = next === true;
                      setMaskPii(on);
                      if (on) setIncludeFiles(false);
                    }}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block">Hide the candidate's own identity too</span>
                    <span className="mt-0.5 block text-[11px] text-text-secondary">For sending the AI record to a vendor. Documents are left out.</span>
                  </span>
                </label>

                <div>
                  <p className="mb-1.5 text-[12px] font-medium text-text-primary">Sections</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {sections.map((s) => (
                      <label key={s.key} className="flex cursor-pointer items-center gap-2 text-[12px] text-text-primary">
                        <Checkbox
                          checked={!!sectionChecks[s.key]}
                          onCheckedChange={(next) =>
                            setSectionChecks((prev) => ({
                              ...prev,
                              [s.key]: next === true,
                            }))
                          }
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-[12px] text-text-secondary" data-testid="export-estimate">
            Estimated size: {formatEstimate(estimatedBytes)} · {estimateSeconds(estimatedBytes)}
          </p>

          {error && (
            <p role="alert" className="rounded-lg border border-tone-rose/30 bg-tone-rose-soft px-3.5 py-2.5 text-[12px] leading-5 text-text-primary">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            variant="outline"
            disabled={exporting}
            onClick={() => onOpenChange(false)}
            className="!h-9 !min-h-0 !rounded-full !px-4 !py-0 text-[12px] font-medium"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={exporting || chosenSections.length === 0}
            onClick={handleExport}
            className="!h-9 !min-h-0 !rounded-full !px-4 !py-0 gap-1.5 text-[12px] font-medium"
          >
            {exporting ? (
              <>
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                Exporting…
              </>
            ) : (
              <>
                <FileDown size={14} aria-hidden="true" />
                Export
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
